import { apiClient } from '@api'

/**
 * Certificate Service
 *
 * Service layer for managing compliance certificates
 * Provides methods for CRUD operations on certificates
 */
class CertificateService {
	/**
	 * Get all certificates
	 * @param {Object} params - Query parameters (page, limit, status, search)
	 * @returns {Promise<Object>} Certificates data with pagination meta
	 */
	async getAll(params = {}) {
		const queryParams = new URLSearchParams()

		if (params.page) queryParams.append('page', params.page)
		if (params.limit) queryParams.append('limit', params.limit)
		if (params.status) queryParams.append('status', params.status)
		if (params.search) queryParams.append('search', params.search)
		if (params.sort) queryParams.append('sort', params.sort)

		const queryString = queryParams.toString()
		const endpoint = queryString ? `/certificates?${queryString}` : '/certificates'

		return await apiClient.get(endpoint)
	}

	/**
	 * Get certificate by ID
	 * @param {string|number} id - Certificate ID
	 * @returns {Promise<Object>} Certificate data
	 */
	async getById(id) {
		return await apiClient.get(`/certificates/${id}`)
	}

	/**
	 * Get certificate by slug
	 * @param {string} slug - Certificate slug
	 * @returns {Promise<Object>} Certificate data
	 */
	async getBySlug(slug) {
		return await apiClient.get(`/certificates/slug/${slug}`)
	}

	/**
	 * Create new certificate
	 * @param {Object} certificateData - Certificate data
	 * @returns {Promise<Object>} Created certificate data
	 */
	async create(certificateData) {
		return await apiClient.post('/certificates', certificateData)
	}

	/**
	 * Update certificate
	 * @param {string|number} id - Certificate ID
	 * @param {Object} certificateData - Updated certificate data
	 * @returns {Promise<Object>} Updated certificate data
	 */
	async update(id, certificateData) {
		return await apiClient.put(`/certificates/${id}`, certificateData)
	}

	/**
	 * Delete certificate (soft delete)
	 * @param {string|number} id - Certificate ID
	 * @returns {Promise<Object>} Success response
	 */
	async delete(id) {
		return await apiClient.delete(`/certificates/${id}`)
	}

	/**
	 * Restore archived certificate
	 * @param {string|number} id - Certificate ID
	 * @returns {Promise<Object>} Restored certificate data
	 */
	async restore(id) {
		return await apiClient.post(`/certificates/${id}/restore`, {})
	}

	/**
	 * Get active certificates only
	 * @returns {Promise<Object>} Active certificates data
	 */
	async getActive() {
		return await apiClient.get('/certificates?status=active')
	}

	/**
	 * Get certificates by array of IDs
	 * @param {Array<string|number>} ids - Array of certificate IDs
	 * @returns {Promise<Object>} Certificates data
	 */
	async getByIds(ids) {
		if (!ids || ids.length === 0) {
			return { success: true, data: [] }
		}
		// Fetch all certificates and filter by IDs
		// This is a client-side filter since backend may not have a bulk endpoint
		const response = await this.getAll({ limit: 100 })
		if (response.success && response.data) {
			const certificates = Array.isArray(response.data) ? response.data : response.data.certificates || []
			const filteredCertificates = certificates.filter((cert) =>
				ids.includes(cert.id) || ids.includes(String(cert.id)) || ids.includes(Number(cert.id))
			)
			return { success: true, data: filteredCertificates }
		}
		return response
	}
}

// Export singleton instance
export const certificateService = new CertificateService()
export default certificateService
